import type { CSSProperties } from 'react';

export type ViewerSurfacePreset =
  | 'auto'
  | 'transparent'
  | 'clean-light'
  | 'soft-warm'
  | 'studio-mist'
  | 'dark-premium'
  | 'asset-image';

export type ViewerSurfaceVariant =
  | 'model-inline'
  | 'model-immersive'
  | 'customiser-inline'
  | 'customiser-fullscreen';

export type ViewerSurfaceTone = 'light' | 'dark';

export type ViewerSurfaceConfig = {
  preset: ViewerSurfacePreset;
  assetUrl: string;
  assetId: string | null;
};

export type ViewerSurfacePresentation = {
  shellStyle: CSSProperties | undefined;
  canvasColor: string;
  transparentCanvas: boolean;
  tone: ViewerSurfaceTone;
};

type BasePresentation = {
  shellStyle: CSSProperties;
  canvasColor: string;
  tone: ViewerSurfaceTone;
};

export const VIEWER_SURFACE_PRESET_OPTIONS: Array<{ value: ViewerSurfacePreset; label: string }> = [
  { value: 'auto', label: 'Use Viewer Default' },
  { value: 'transparent', label: 'Transparent' },
  { value: 'clean-light', label: 'Clean Light' },
  { value: 'soft-warm', label: 'Soft Warm' },
  { value: 'studio-mist', label: 'Studio Mist' },
  { value: 'dark-premium', label: 'Dark Premium' },
  { value: 'asset-image', label: 'Asset Library Image' },
];

const VIEWER_SURFACE_PRESET_SET = new Set<ViewerSurfacePreset>(
  VIEWER_SURFACE_PRESET_OPTIONS.map((option) => option.value)
);

const DEFAULT_VIEWER_SURFACE_PRESENTATIONS: Record<ViewerSurfaceVariant, BasePresentation> = {
  'model-inline': {
    shellStyle: {
      backgroundColor: '#f8f6f2',
      backgroundImage:
        'radial-gradient(circle at 50% 12%, rgba(196, 181, 253, 0.18), transparent 24%), linear-gradient(180deg, #fafaf9 0%, #f5f5f4 100%)',
    },
    canvasColor: '#f5f5f4',
    tone: 'light',
  },
  'model-immersive': {
    shellStyle: {
      backgroundColor: '#0c0a09',
      backgroundImage:
        'radial-gradient(circle at 50% 18%, rgba(255, 255, 255, 0.08), transparent 28%), linear-gradient(180deg, #0f172a 0%, #0c0a09 100%)',
    },
    canvasColor: '#0c0a09',
    tone: 'dark',
  },
  'customiser-inline': {
    shellStyle: {
      backgroundColor: '#f5f5f4',
      backgroundImage:
        'radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.72), transparent 34%), linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
    },
    canvasColor: '#f1f5f9',
    tone: 'light',
  },
  'customiser-fullscreen': {
    shellStyle: {
      backgroundColor: '#0c0a09',
      backgroundImage:
        'radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.08), transparent 30%), linear-gradient(180deg, #18181b 0%, #09090b 100%)',
    },
    canvasColor: '#0c0a09',
    tone: 'dark',
  },
};

const NAMED_VIEWER_SURFACE_PRESENTATIONS: Record<
  Exclude<ViewerSurfacePreset, 'auto' | 'transparent' | 'asset-image'>,
  BasePresentation
> = {
  'clean-light': {
    shellStyle: {
      backgroundColor: '#f5f3ef',
      backgroundImage:
        'radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.82), transparent 34%), linear-gradient(180deg, #fcfbf9 0%, #efe9e0 100%)',
    },
    canvasColor: '#efe9e0',
    tone: 'light',
  },
  'soft-warm': {
    shellStyle: {
      backgroundColor: '#efe3d2',
      backgroundImage:
        'radial-gradient(circle at 22% 16%, rgba(255, 255, 255, 0.7), transparent 28%), radial-gradient(circle at 78% 18%, rgba(255, 244, 214, 0.56), transparent 22%), linear-gradient(145deg, #faf2e5 0%, #e7d3bc 100%)',
    },
    canvasColor: '#ead8c2',
    tone: 'light',
  },
  'studio-mist': {
    shellStyle: {
      backgroundColor: '#dbe4ee',
      backgroundImage:
        'radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.78), transparent 30%), linear-gradient(180deg, #edf2f7 0%, #d9e2ec 100%)',
    },
    canvasColor: '#d9e2ec',
    tone: 'light',
  },
  'dark-premium': {
    shellStyle: {
      backgroundColor: '#111827',
      backgroundImage:
        'radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.12), transparent 26%), linear-gradient(180deg, #1f2937 0%, #020617 100%)',
    },
    canvasColor: '#0f172a',
    tone: 'dark',
  },
};

const escapeCssUrl = (value: string) => value.replace(/(["\\)])/g, '\\$1');

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

export function resolveViewerSurfaceConfig(content: unknown): ViewerSurfaceConfig {
  const record = isRecord(content) ? content : {};
  const preset = VIEWER_SURFACE_PRESET_SET.has(record.viewerBackgroundPreset as ViewerSurfacePreset)
    ? (record.viewerBackgroundPreset as ViewerSurfacePreset)
    : 'auto';
  const assetUrl =
    typeof record.viewerBackgroundAssetUrl === 'string' ? record.viewerBackgroundAssetUrl.trim() : '';
  const assetId =
    typeof record.viewerBackgroundAssetId === 'string' && record.viewerBackgroundAssetId.trim().length > 0
      ? record.viewerBackgroundAssetId.trim()
      : null;
  return { preset, assetUrl, assetId };
}

export function resolveViewerSurfacePresentation(
  content: unknown,
  variant: ViewerSurfaceVariant
): ViewerSurfacePresentation {
  const config = resolveViewerSurfaceConfig(content);
  const basePresentation =
    config.preset === 'auto' || config.preset === 'transparent'
      ? DEFAULT_VIEWER_SURFACE_PRESENTATIONS[variant]
      : config.preset === 'asset-image'
        ? DEFAULT_VIEWER_SURFACE_PRESENTATIONS[variant]
        : NAMED_VIEWER_SURFACE_PRESENTATIONS[config.preset];

  if (config.preset === 'auto') {
    return {
      shellStyle: undefined,
      canvasColor: basePresentation.canvasColor,
      transparentCanvas: false,
      tone: basePresentation.tone,
    };
  }

  if (config.preset === 'transparent') {
    return {
      shellStyle: undefined,
      canvasColor: basePresentation.canvasColor,
      transparentCanvas: true,
      tone: basePresentation.tone,
    };
  }

  if (config.preset === 'asset-image' && config.assetUrl) {
    return {
      shellStyle: {
        backgroundColor: basePresentation.canvasColor,
        backgroundImage: `linear-gradient(180deg, rgba(15, 23, 42, 0.16), rgba(15, 23, 42, 0.32)), url("${escapeCssUrl(
          config.assetUrl
        )}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      },
      canvasColor: basePresentation.canvasColor,
      transparentCanvas: true,
      tone: basePresentation.tone,
    };
  }

  return {
    shellStyle: basePresentation.shellStyle,
    canvasColor: basePresentation.canvasColor,
    transparentCanvas: true,
    tone: basePresentation.tone,
  };
}
